import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import { client } from "../lib/client.js";
import { useEventListener, useEvents } from "../lib/events.tsx";

/**
 * Canlı iş logu. Gradle ve EAS çıktısı ANSI renk/kontrol karakteri içerir; düz DOM
 * ile okunmaz hale gelir, o yüzden gerçek bir terminal emülatörü kullanıyoruz.
 *
 * Akış iki kaynaktan gelir:
 *  1. Sayfa açılırken REST ile geçmiş satırlar (yeniden bağlanan istemci kaçırdığını
 *     toparlasın diye `seq` imleçli).
 *  2. Sonrasında WebSocket olayları.
 * `seq` takibi ikisinin çakışmasını engeller — aynı satır iki kez yazılmaz.
 * Olay akışı koparsa (ör. ağ) iş sürerken REST ile yoklanır; log donmuş görünmez.
 */
export function LogView({
  jobId,
  active = false,
  height = 430,
}: {
  jobId: string;
  /** İş sürüyor mu — yalnızca o zaman yoklanır. */
  active?: boolean;
  height?: number;
}): ReactNode {
  const host = useRef<HTMLDivElement>(null);
  const term = useRef<Terminal | null>(null);
  const lastSeq = useRef(-1);
  const { connected } = useEvents();

  useEffect(() => {
    if (!host.current) return;
    // Başka bir işe geçildi: önceki işin imleci kalırsa yeni işin ilk satırları
    // "zaten görüldü" sanılıp atlanır.
    lastSeq.current = -1;

    const terminal = new Terminal({
      convertEol: true,
      fontSize: 12,
      fontFamily: 'ui-monospace, "Cascadia Code", "JetBrains Mono", Consolas, monospace',
      theme: {
        background: "#00000000", // saydam — .log-surface zemini görünsün
        foreground: "#cfd3e5",
        cursor: "#9184d9",
        selectionBackground: "#9184d960",
      },
      scrollback: 20_000,
      cursorInactiveStyle: "none",
      disableStdin: true,
    });

    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.open(host.current);
    term.current = terminal;

    // xterm, render edilmeden ya da dispose sonrası fit() çağrılınca "dimensions"
    // hatası fırlatıyor (StrictMode'un çift mount'u ve gizli sekmede sık). Bir
    // sonraki kareye ertelenir ve kapanmışsa atlanır.
    let disposed = false;
    let frame = 0;
    const safeFit = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (disposed) return;
        try {
          fit.fit();
        } catch {
          /* görünür değilken ölçü yok; bir sonraki boyut değişiminde tekrar denenir */
        }
      });
    };
    safeFit();

    const observer = new ResizeObserver(safeFit);
    observer.observe(host.current);

    let cancelled = false;
    void client.jobLogs(jobId).then((logs) => {
      if (cancelled) return;
      for (const log of logs) {
        if (log.seq <= lastSeq.current) continue;
        lastSeq.current = log.seq;
        terminal.writeln(paint(log.stream, log.chunk));
      }
    });

    return () => {
      cancelled = true;
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      terminal.dispose();
      term.current = null;
    };
  }, [jobId]);

  useEventListener(
    (event) => {
      if (event.t !== "job.log" || event.jobId !== jobId) return;
      if (event.seq <= lastSeq.current) return;
      lastSeq.current = event.seq;
      term.current?.writeln(paint(event.stream, event.chunk));
    },
    [jobId],
  );

  useEffect(() => {
    if (connected || !active) return;
    const timer = setInterval(() => {
      void client.jobLogs(jobId, lastSeq.current).then((logs) => {
        for (const log of logs) {
          if (log.seq <= lastSeq.current) continue;
          lastSeq.current = log.seq;
          term.current?.writeln(paint(log.stream, log.chunk));
        }
      });
    }, 3000);
    return () => clearInterval(timer);
  }, [connected, active, jobId]);

  // ⚠ Terminal MUTLAK konumlu iç kutuda: xterm kendi genişliğini sütun sayısından
  // piksel olarak yazıyor. Akışta kalırsa kapsayıcıyı içerik kadar büyütüyor, fit()
  // daha geniş ölçüp daha çok sütun açıyor ve sayfa sonsuza dek sağa genişliyordu
  // (ekran ölçeğine bağlı; her makinede tetiklenmiyor). Mutlak çocuk ebeveynin
  // boyutunu etkileyemez → döngü kurulamaz.
  return (
    <div className="log-surface relative w-full min-w-0 overflow-hidden" style={{ height }}>
      <div ref={host} className="absolute inset-0 px-[15px] py-3.5" />
    </div>
  );
}

/** Agent'ın kendi mesajları ve stderr renklendirilir; komut çıktısı olduğu gibi kalır. */
const ESC = String.fromCharCode(27); // kaynakta ham kontrol karakteri bırakmamak için
const RESET = `${ESC}[0m`;

function paint(stream: string, chunk: string): string {
  if (stream === "system") return `${ESC}[38;5;111m${chunk}${RESET}`;
  if (stream === "stderr") return `${ESC}[38;5;210m${chunk}${RESET}`;
  return chunk;
}
